<template>
  <Transition name="notification">
    <div v-if="show" class="update-notification-overlay">
      <div class="update-notification">
        <div class="notification-content">
          <div class="notification-icon">
            <i class="pi pi-sync"></i>
          </div>

          <div class="notification-text">
            <h3>¡Nueva actualización disponible!</h3>
            <p>
              Versión <strong>{{ newVersion }}</strong> lista para instalar
            </p>
            <p v-if="dismissCount > 0" class="reminder-text">
              Recordatorio {{ dismissCount }} de 3
            </p>
          </div>
        </div>

        <div class="notification-actions">
          <button v-if="dismissCount < 3" @click="later" class="btn-secondary">
            <i class="pi pi-clock"></i>
            Más tarde ({{ dismissCount }}/3)
          </button>
          <button @click="update" class="btn-primary">
            <i class="pi pi-refresh"></i>
            Actualizar ahora
          </button>
        </div>

        <button v-if="dismissCount < 3" @click="later" class="btn-close">
          <i class="pi pi-times"></i>
        </button>
      </div>
    </div>
  </Transition>
</template>

<script>
import versionChecker from "@/utils/versionChecker";

export default {
  name: "UpdateNotification",
  data() {
    return {
      show: false,
      newVersion: "",
      dismissCount: 0,
      reminderTimeout: null,
    };
  },
  async mounted() {
    try {
      await versionChecker.init();

      this.dismissCount = versionChecker.getDismissCount();

      versionChecker.onUpdate((version) => {
        this.newVersion = version;
        this.dismissCount = versionChecker.getDismissCount();
        this.show = true;
      });

      versionChecker.start();
    } catch (error) {
      // Error silencioso
    }
  },
  beforeUnmount() {
    versionChecker.stop();
    if (this.reminderTimeout) {
      clearTimeout(this.reminderTimeout);
    }
  },
  methods: {
    update() {
      versionChecker.reload();
    },
    later() {
      const canContinue = versionChecker.notifyLater();
      this.dismissCount = versionChecker.getDismissCount();

      this.show = false;

      if (!canContinue) {
        return;
      }

      this.reminderTimeout = setTimeout(() => {
        this.show = true;
      }, 180000);
    },
  },
};
</script>

<style scoped>
.update-notification-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.update-notification {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  padding: 32px;
  max-width: 500px;
  width: 100%;
  position: relative;
  border: 2px solid #667eea;
}

.notification-content {
  display: flex;
  gap: 20px;
  margin-bottom: 24px;
  align-items: center;
}

.notification-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 28px;
  animation: iconPulse 2s ease-in-out infinite;
}

@keyframes iconPulse {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(102, 126, 234, 0.7);
  }
  50% {
    transform: scale(1.05);
    box-shadow: 0 0 0 10px rgba(102, 126, 234, 0);
  }
}

.notification-text {
  flex: 1;
}

.notification-text h3 {
  margin: 0 0 8px 0;
  font-size: 20px;
  font-weight: 700;
  color: #1a202c;
}

.notification-text p {
  margin: 0;
  font-size: 15px;
  color: #718096;
}

.notification-text p strong {
  color: #667eea;
  font-weight: 700;
}

.reminder-text {
  margin-top: 8px !important;
  font-size: 13px !important;
  color: #f59e0b !important;
  font-weight: 600 !important;
}

.notification-actions {
  display: flex;
  gap: 12px;
}

.btn-secondary,
.btn-primary {
  flex: 1;
  padding: 14px 24px;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-secondary {
  background: #f7fafc;
  color: #4a5568;
  border: 2px solid #e2e8f0;
}

.btn-secondary:hover {
  background: #edf2f7;
  border-color: #cbd5e0;
  transform: translateY(-2px);
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
}

.btn-close {
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: none;
  color: #a0aec0;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  font-size: 20px;
  transition: all 0.2s ease;
}

.btn-close:hover {
  background: #f7fafc;
  color: #4a5568;
}

.notification-enter-active {
  animation: fadeInScale 0.4s ease-out;
}

.notification-leave-active {
  animation: fadeOutScale 0.3s ease-in;
}

@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes fadeOutScale {
  from {
    opacity: 1;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(0.95);
  }
}

@media (max-width: 640px) {
  .update-notification {
    padding: 24px;
    max-width: calc(100vw - 40px);
  }

  .notification-icon {
    width: 56px;
    height: 56px;
    font-size: 24px;
  }

  .notification-text h3 {
    font-size: 18px;
  }

  .notification-actions {
    flex-direction: column;
  }
}
</style>
